/**
 * Yönetim → Kanıt Havuzu sekmesi.
 *
 * Kalıcı araştırma korpusunun operatör yüzeyi. Bir arşivin içine
 * bakılamıyorsa doğru çalıştığına güvenilemez — bu sekmenin var olma sebebi
 * o güveni ölçülebilir kılmak.
 *
 * Öne çıkarılan metrik TEKRAR ORANI. 2026-07-25 denetimi bu oranı %63 olarak
 * ölçtü: sistem aynı sayfaları tekrar tekrar indiriyordu (bir Google News
 * bağlantısı 21 kez). Havuz o tekrarı yakaladıkça "yeniden kullanım" sayacı
 * artar; operatör özelliğin işe yarayıp yaramadığını tahmin etmek zorunda
 * kalmaz.
 */
import { useState, useEffect, useCallback } from "react";
import type { CorpusStats, CorpusItem } from "../../transport/authClient";
import {
  getCorpusStats,
  listCorpus,
  pruneCorpus,
  embedCorpus,
} from "../../transport/authClient";
import { Pager } from "../library/ui";

// Bu etiketler bir BAYATLAMA sınıfını gösterir: içeriğin ne kadar sürede
// doğruluğunu yitirdiğini. «Mevzuat» yalnızca hukuki metnin kendisi içindir;
// bir bakanlık alan adındaki proje sayfası, faaliyet raporu veya giriş ekranı
// «Resmî»dir. İkisi tek etikete sıkıştırıldığında bir iş başvurusu sonuç
// ekranı «Mevzuat» olarak ve süresiz kalıcı olarak görünüyordu.
const CLASS_LABELS: Record<string, string> = {
  regulation: "Mevzuat",
  official: "Resmî",
  academic: "Akademik",
  reference: "Referans",
  news: "Haber",
  social: "Sosyal",
  market: "Piyasa",
};

const CLASS_HINTS: Record<string, string> = {
  regulation: "Hukuki metin (kanun, yönetmelik, Resmî Gazete) — bayatlamaz.",
  official: "Resmî kurum yayını; hukuki metin değil — 180 günde bayatlar.",
  academic: "Akademik/ansiklopedik kaynak — bayatlamaz.",
  reference: "Genel başvuru kaynağı — 90 günde bayatlar.",
  news: "Haber — 48 saatte bayatlar.",
  social: "Sosyal medya — 24 saatte bayatlar.",
  market: "Piyasa verisi — 7 günde bayatlar.",
};

const CLASS_COLORS: Record<string, string> = {
  regulation: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  official: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  academic: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  reference: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  news: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  social: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  market: "bg-violet-500/15 text-violet-300 border-violet-500/30",
};

/** Sunucu sayfa boyutu — `Pager` toplamı tam bu sayıya böler. */
const PAGE_SIZE = 60;

export function CorpusTab() {
  const [stats, setStats] = useState<CorpusStats | null>(null);
  const [items, setItems] = useState<CorpusItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  // Yazılan metin ile SORGULANAN metin ayrı: her tuş vuruşunda korpusa ILIKE
  // sorgusu atmak hem sunucuyu hem sayfalayıcıyı boşa çalıştırır.
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [klass, setKlass] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (draft.trim() === q) return;
    const t = setTimeout(() => {
      setQ(draft.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(t);
  }, [draft, q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, l] = await Promise.all([
        getCorpusStats("all"),
        listCorpus({ q, stalenessClass: klass, limit: PAGE_SIZE, offset }),
      ]);
      setStats(s);
      setItems(l.items);
      setTotal(l.total);
      // Bir «Bayatlamışları Sil» işlemi listeyi mevcut ofsetin altına
      // düşürebilir; sayfalayıcı "sayfa 4 / 2" derken liste boş kalır.
      if (l.items.length === 0 && offset > 0) {
        setOffset(Math.max(0, (Math.max(1, Math.ceil(l.total / PAGE_SIZE)) - 1) * PAGE_SIZE));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Havuz okunamadı");
    } finally {
      setLoading(false);
    }
  }, [q, klass, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Filtre değişimi ofseti geçersiz kılar: yeni küme çoğunlukla daha kısadır. */
  function pickClass(next: string) {
    setKlass(next);
    setOffset(0);
  }

  async function run(action: "prune" | "embed") {
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      if (action === "prune") {
        const r = await pruneCorpus();
        setNotice(`${r.removed} bayatlamış kayıt silindi.`);
      } else {
        const r = await embedCorpus(256);
        setNotice(`${r.embedded} kayıt gömüldü.`);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "İşlem başarısız");
    } finally {
      setBusy(null);
    }
  }

  if (loading && !stats) {
    return <div className="text-center py-12 text-slate-500">Kanıt havuzu yükleniyor…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Kanıt Havuzu</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Tartışmalarda bulunan web kaynaklarının kalıcı arşivi. Her yeni
            tartışma önce buraya bakar; elde olanı yeniden indirmez. Etiketler
            içeriğin türünü değil <strong className="text-slate-400">ne kadar
            sürede bayatladığını</strong> söyler: haber saatler içinde, resmî
            kurum sayfaları aylar içinde bayatlar; yalnızca hukuki metin ve
            akademik kaynaklar kalıcıdır.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => void run("embed")}
            disabled={busy !== null}
            className="px-3 py-2 text-xs rounded-lg border border-white/10 hover:bg-white/5 disabled:opacity-40 transition-colors"
          >
            {busy === "embed" ? "Gömülüyor…" : "Gömmeyi Tamamla"}
          </button>
          <button
            onClick={() => void run("prune")}
            disabled={busy !== null}
            className="px-3 py-2 text-xs rounded-lg border border-white/10 hover:bg-white/5 disabled:opacity-40 transition-colors"
          >
            {busy === "prune" ? "Temizleniyor…" : "Bayatlamışları Sil"}
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300">
          {error}
        </div>
      )}
      {notice && (
        <div className="px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-300">
          {notice}
        </div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Metric label="Kaynak" value={stats.total.toLocaleString("tr-TR")} />
            <Metric label="Alan adı" value={stats.domains.toLocaleString("tr-TR")} />
            <Metric
              label="Tekrar oranı"
              value={`${stats.redundancy_pct}%`}
              hint="Aynı kaynağın kaç kez yeniden bulunduğu — havuz olmasa bu iş boşa giderdi"
              accent="amber"
            />
            <Metric
              label="Yeniden kullanım"
              value={stats.reuses.toLocaleString("tr-TR")}
              hint="Havuzun web aramasından kurtardığı çağrı sayısı"
              accent="emerald"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Metric
              label="Arşivlenen metin"
              value={`${(stats.chars / 1_000_000).toFixed(1)} M karakter`}
            />
            <Metric
              label="Gömülmemiş"
              value={stats.unembedded.toLocaleString("tr-TR")}
              hint="Vektörü olmayan kayıtlar anlamsal aramada görünmez"
              accent={stats.unembedded > 0 ? "amber" : "slate"}
            />
            <Metric
              label="Bayatlamış"
              value={stats.expired.toLocaleString("tr-TR")}
              hint="Süresi geçmiş; geri çağırmaya dahil edilmez"
              accent={stats.expired > 0 ? "amber" : "slate"}
            />
          </div>

          <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
            <h3 className="font-semibold text-sm mb-4">Tazelik Sınıfları</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_class).map(([k, n]) => (
                <button
                  key={k}
                  title={CLASS_HINTS[k] ?? "Bayatlama sınıfı"}
                  onClick={() => pickClass(klass === k ? "" : k)}
                  className={`px-3 py-1.5 rounded-lg border text-xs transition-all ${
                    CLASS_COLORS[k] ?? CLASS_COLORS.reference
                  } ${klass === k ? "ring-1 ring-white/40" : "opacity-80 hover:opacity-100"}`}
                >
                  {CLASS_LABELS[k] ?? k} · {n}
                </button>
              ))}
              {klass && (
                <button
                  onClick={() => pickClass("")}
                  className="px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-white"
                >
                  filtreyi kaldır
                </button>
              )}
            </div>
          </section>

          <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
            <h3 className="font-semibold text-sm mb-4">En Çok Kaynak Veren Alanlar</h3>
            <div className="space-y-1.5">
              {stats.top_domains.map((d) => (
                <div key={d.domain} className="flex items-center gap-3">
                  <code className="text-xs text-slate-300 w-64 truncate" title={d.domain}>
                    {d.domain || "—"}
                  </code>
                  <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500"
                      style={{
                        width: `${Math.min(100, (d.count / (stats.top_domains[0]?.count || 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 tabular-nums w-16 text-right">
                    {d.count}
                  </span>
                  <span
                    className="text-[10px] text-emerald-400 tabular-nums w-20 text-right"
                    title="Bu alandan kaç kez yeniden kullanım oldu"
                  >
                    {d.reuses > 0 ? `↻ ${d.reuses}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <h3 className="font-semibold text-sm">Arşiv</h3>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="başlık, özet veya adreste ara…"
            className="ml-auto w-72 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">
            {q || klass
              ? "Bu filtreyle kayıt bulunamadı. Arama tüm havuzda çalışır, yalnız görünen sayfada değil."
              : "Kayıt bulunamadı."}
          </p>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((it) => (
              <div key={it.url} className="py-3 flex items-start gap-3">
                <span
                  title={CLASS_HINTS[it.stalenessClass] ?? "Bayatlama sınıfı"}
                  className={`shrink-0 mt-0.5 px-2 py-0.5 rounded text-[10px] border ${
                    CLASS_COLORS[it.stalenessClass] ?? CLASS_COLORS.reference
                  }`}
                >
                  {CLASS_LABELS[it.stalenessClass] ?? it.stalenessClass}
                </span>
                <div className="min-w-0 flex-1">
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm text-slate-200 hover:text-indigo-300 transition-colors line-clamp-1"
                    title={it.title}
                  >
                    {it.title || it.url}
                  </a>
                  <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
                    {it.domain} · {it.charCount.toLocaleString("tr-TR")} karakter
                    {it.expiresAt
                      ? ` · ${new Date(it.expiresAt).toLocaleDateString("tr-TR")} tarihinde bayatlar`
                      : " · kalıcı"}
                  </p>
                </div>
                <div className="shrink-0 flex items-center gap-3 text-[10px] tabular-nums">
                  <span className="text-slate-500" title="Kaç kez bulundu">
                    {it.hitCount}× bulundu
                  </span>
                  {it.reuseCount > 0 && (
                    <span
                      className="text-emerald-400"
                      title="Havuzdan kaç kez yeniden kullanıldı"
                    >
                      ↻ {it.reuseCount}
                    </span>
                  )}
                  {!it.embedded && (
                    <span
                      className="text-amber-400"
                      title="Vektörü yok — anlamsal aramada görünmez"
                    >
                      gömülmemiş
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {total > 0 && (
          <div className="mt-4">
            <Pager
              offset={offset}
              limit={PAGE_SIZE}
              total={total}
              busy={loading}
              unit="kaynak"
              onChange={setOffset}
            />
          </div>
        )}
      </section>
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
  accent = "slate",
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: "slate" | "amber" | "emerald";
}) {
  const color =
    accent === "amber"
      ? "text-amber-300"
      : accent === "emerald"
        ? "text-emerald-300"
        : "text-white";
  return (
    <div
      className="bg-white/[0.02] border border-white/5 rounded-xl px-4 py-3"
      {...(hint ? { title: hint } : {})}
    >
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className={`text-lg font-semibold mt-0.5 tabular-nums ${color}`}>{value}</p>
      {hint && <p className="text-[10px] text-slate-600 mt-1 line-clamp-2">{hint}</p>}
    </div>
  );
}
